import { FormEvent, useMemo, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DEFAULT_DEMO_VARIANT_SLUG,
  getDemoTestableVariants,
} from '@aldebaran/common/qVariants';

import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ResponsePhoneField } from '@/components/ResponsePhoneField';
import { getDemoLaunchErrorMessage } from '@/components/DemoLead/demoLaunchError';
import { isValidEmail } from '@/util/email';

export interface DemoLeadValues {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  speciality: string;
  variantSlug: string;
}

export const DemoLeadModal = ({
  defaultVariantSlug,
  onSubmit,
}: {
  defaultVariantSlug?: string;
  onSubmit: (values: DemoLeadValues) => void | Promise<void>;
}) => {
  const variantSlug = useMemo(
    () =>
      getDemoTestableVariants().find((v) => v.slug === defaultVariantSlug)
        ?.slug ?? DEFAULT_DEMO_VARIANT_SLUG,
    [defaultVariantSlug],
  );

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [speciality, setSpeciality] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPhoneInvalid = !!phone && !isValidPhoneNumber(phone);

  const isValid =
    !isPhoneInvalid &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    speciality.trim().length > 0 &&
    isValidEmail(email.trim());

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Launching takes seconds (demo request + logout) before the page
    // navigates away — the guard keeps a double-click from launching twice
    if (!isValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone: phone || undefined,
        speciality: speciality.trim(),
        variantSlug,
      });
    } catch (submitError) {
      // Nothing was launched — keep the filled-in form and let them retry
      setError(getDemoLaunchErrorMessage(submitError));
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open>
      <DialogContent className="p-6">
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-indigo-900 text-center">
              Testez Aldebaran comme <br />
              l&apos;un de vos patients
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 nsm:flex-row">
              <Field className="flex-1">
                <FieldLabel>Prénom</FieldLabel>
                <Input
                  name="firstName"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Marie"
                  required
                />
              </Field>
              <Field className="flex-1">
                <FieldLabel>Nom</FieldLabel>
                <Input
                  name="lastName"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Curie"
                  required
                />
              </Field>
            </div>

            <Field>
              <FieldLabel>Email</FieldLabel>
              <Input
                name="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="marie.curie@email.com"
                required
              />
              <FieldDescription className="px-1 text-xs text-muted-foreground">
                Nous vous enverrons la synthèse médicale générée à cette adresse
              </FieldDescription>
            </Field>

            <Field>
              <FieldLabel>Téléphone (optionnel)</FieldLabel>
              <ResponsePhoneField
                label="06 12 34 56 78"
                value={phone}
                onChange={(value) => {
                  setPhone(value);
                  setPhoneError(false);
                }}
                onBlur={() => setPhoneError(isPhoneInvalid)}
                error={phoneError}
              />
              {phoneError && (
                <FieldDescription className="px-1 text-xs text-red-600">
                  Numéro de téléphone invalide.
                </FieldDescription>
              )}
            </Field>

            <Field>
              <FieldLabel>Spécialité</FieldLabel>
              <Input
                name="speciality"
                value={speciality}
                onChange={(e) => setSpeciality(e.target.value)}
                placeholder="Médecin généraliste"
                required
              />
            </Field>
          </div>

          <div className="flex flex-col items-center gap-3">
            {error && (
              <p role="alert" className="text-center text-sm text-red-600">
                {error}
              </p>
            )}
            <Button
              type="submit"
              variant="ctaPrimary"
              size="lg"
              className="w-full"
              disabled={!isValid || isSubmitting}
            >
              {isSubmitting ? 'Lancement en cours…' : 'Tester la solution'}
            </Button>
            <p className="text-center text-xs text-muted-foreground">
              En lançant le questionnaire, vous acceptez d&apos;être recontacté
              par l&apos;équipe Aldebaran
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
